import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PetProvider } from './context/PetContext';
import { DataProvider } from './context/DataContext';
import { BeanProvider } from './context/BeanContext';
import { RecordsTabProvider } from './context/RecordsTabContext';
import Layout from './components/Layout';
import RemovedPetNoticeDialog from './components/RemovedPetNoticeDialog';

// Pages
import SplashScreen from './pages/SplashScreen';
import Login from './pages/Login';
import Register from './pages/Register';
import Vitals from './pages/Vitals';
import Meds from './pages/Meds';
import Library from './pages/Library';
import Settings from './pages/Settings';
import Trends from './pages/Trends';
import KodaFellowship from './pages/KodaFellowship';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import AuthAction from './pages/AuthAction';
import ComingSoon from './pages/ComingSoon';
import NotFound from './pages/NotFound';
import WebsiteApp from '../website/App';
import { Navbar } from '../website/components/Navbar';
import { PetSittersNavbar } from '../website/components/PetSittersNavbar';
import { Footer } from '../website/components/Footer';
import { CookieConsent } from '../website/components/CookieConsent';
import { PetSitters } from '../website/pages/PetSitters';
import { useLanguage } from './functions/useLanguage';
import { getLanguageTag } from './functions/i18n';
import { UpdatePrompt } from './components/UpdatePrompt';
import { PushNotificationRegistrar } from './components/PushNotificationRegistrar';

function WebsiteShell({ children, usePetSittersNavbar = false }: { children: React.ReactNode; usePetSittersNavbar?: boolean }) {
    return (
        <div className="min-h-screen flex flex-col font-sans">
            {usePetSittersNavbar ? <PetSittersNavbar /> : <Navbar />}
            <div className="flex-1">{children}</div>
            <Footer />
            <CookieConsent />
        </div>
    );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, firebaseUser } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (firebaseUser && !firebaseUser.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
    const { user, authLoading } = useAuth();
    const { language } = useLanguage();
    const location = useLocation();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

    // Persist last visited protected page so SplashScreen can return the user there
    const AUTH_PATHS = ['/login', '/register', '/verify-email', '/forgot-password', '/splash', '/auth'];
    useEffect(() => {
        if (!AUTH_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
            localStorage.setItem('kc_lastPage', location.pathname);
        }
    }, [location.pathname]);
    const isProductionWebsiteHost =
        hostname === 'kodacare.pet' ||
        hostname === 'www.kodacare.pet';
    const isWebsiteHost =
        isProductionWebsiteHost ||
        hostname.includes('kodacare-website');

    useEffect(() => {
        document.documentElement.lang = getLanguageTag(language);
    }, [language]);

    // Wait for Firebase Auth to resolve before rendering routes.
    // Without this gate the ProtectedRoute would flash-redirect to /login
    // on every cold load while onAuthStateChanged is still pending.
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="w-8 h-8 border-2 border-primary-brown border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <Routes>
            {/* Auth action handler MUST come before the website wildcard so that
                Firebase email links (verifyEmail / resetPassword / recoverEmail)
                resolve to the in-app handler on the kodacare.pet host. */}
            <Route path="/auth/action" element={<AuthAction />} />

            {/* On kodacare.pet and kodacare-website.web.app: serve full website */}
            {isWebsiteHost && <Route path="/*" element={<WebsiteApp />} />}

            {/* App-only routes — not accessible on the marketing domain */}
            {!isWebsiteHost && <>
                <Route path="/splash" element={<SplashScreen />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />

                {/* Patient Routes */}
                <Route path="/records" element={<Navigate to="/vitals" replace />} />
                <Route path="/vitals" element={
                    <ProtectedRoute>
                        <Layout><Vitals /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/meds" element={
                    <ProtectedRoute>
                        <Layout><Meds /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/guide" element={<Navigate to="/library" replace />} />
                <Route path="/library" element={
                    <ProtectedRoute>
                        <Layout><Library /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/guide/:section" element={<Navigate to="/library" replace />} />
                <Route path="/library/:section" element={
                    <ProtectedRoute>
                        <Layout><Library /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/settings" element={
                    <ProtectedRoute>
                        <Layout><Settings /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/settings/:section" element={
                    <ProtectedRoute>
                        <Layout><Settings /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/notifications" element={<Navigate to="/library/reminders" replace />} />
                <Route path="/settings/reminders" element={<Navigate to="/library/reminders" replace />} />
                <Route path="/options" element={<Navigate to="/settings" replace />} />
                <Route path="/trends" element={
                    <ProtectedRoute>
                        <Layout><Trends /></Layout>
                    </ProtectedRoute>
                } />
                <Route path="/bean" element={<Navigate to="/kodafellowship" replace />} />
                <Route path="/kodafellowship" element={
                    <ProtectedRoute>
                        <Layout><KodaFellowship /></Layout>
                    </ProtectedRoute>
                } />

                {/* Website marketing pages accessible from within the app */}
                <Route path="/website/*" element={<WebsiteApp />} />
                                <Route
                                    path="/petsitters"
                                    element={(
                                        <WebsiteShell usePetSittersNavbar>
                                            <PetSitters />
                                        </WebsiteShell>
                                    )}
                                />

                {/* Default Redirect — always enter via splash so auth resolves before routing */}
                <Route path="/" element={<Navigate to="/splash" replace />} />
                <Route path="*" element={<NotFound />} />
            </>}
        </Routes>
    );
}

export default function App() {
  return (
    <AuthProvider>
        <PetProvider>
            <DataProvider>
                <BeanProvider>
                    <RecordsTabProvider>
                        <Router>
                            <UpdatePrompt />
                            <PushNotificationRegistrar />
                            <AppRoutes />
                            <RemovedPetNoticeDialog />
                        </Router>
                    </RecordsTabProvider>
                </BeanProvider>
            </DataProvider>
        </PetProvider>
    </AuthProvider>
  );
}
